<template>
	<v-container grid-list-xl v-if="show">
		<v-layout
			row
			justify-center
		>
			<v-flex
				xs12
				md8
				pa-0
			>
				<slot name="alert" />
				<NewItem
					v-if="$userCan('manage_keywords') && !project.isArchived"
					ref="newItem"
					button-title="New Keyword List"
				>
					<template slot="form">
						<v-form
							ref="keywordsForm"
							v-model="valid.new"
							@submit.prevent="newKeywordList()"
						>
							<v-layout
								row
								wrap
								ma-0
								justify-center
								align-center
							>
								<v-flex
									xs12
									sm9
									py-0
								>
									<v-text-field
										v-model="name"
										:rules="[
											validateCheckDuplicates(name, keywordListsNames),
											...validate.require,
											validate.max(name, 30)
										]"
										autofocus
										name="Name"
										label="Name"
									/>
								</v-flex>
								<v-flex
									xs12
									sm3
								>
									<v-btn
										:disabled="!valid.new"
										ripple
										color="primary"
										type="submit"
									>
										Submit
									</v-btn>
									<v-btn
										flat
										color="error"
										@click="closeNewList"
									>
										close
									</v-btn>
								</v-flex>
							</v-layout>
						</v-form>
					</template>
				</NewItem>
			</v-flex>
		</v-layout>
		<KeywordIterator
			:items="keywordLists"
			@default="setDefault"
			@add="showAdd"
			@update="showRename"
			@delete="deleteKeywordList"
			@delete-keyword="deleteKeyword"
		/>
		<v-dialog
			v-model="add"
			max-width="800"
		>
			<v-card v-if="add">
				<v-container grid-list-xl>
					<div class="headline pb-2">{{ addList.name }}</div>
					<v-form
						ref="addForm"
						v-model="valid.add"
						@submit.prevent="addKeyword(addList)"
					>
						<v-layout
							row
							wrap
							align-center
							justify-center
						>
							<v-flex
								xs12
								sm9
								md8
								py-0
							>
								<v-text-field
									v-model="addList.new"
									:rules="
										[validateCheckDuplicates(addList.new, addListKeywords),
											...validate.require, validate.max(addList.new, 40)]"
									autofocus
									name="keyword"
									label="Add New Keyword"
								/>
							</v-flex>
							<v-flex
								sm3
								md2
								class="text-xs-center"
							>
								<v-btn
									:disabled="!valid.add"
									flat
									type="submit"
								>
									Add
								</v-btn>
								<v-btn
									flat
									color="error"
									@click="add = false"
								>
									close
								</v-btn>
							</v-flex>
						</v-layout>
					</v-form>
					<v-layout
						row
						wrap
						justify-center
						align-center
					>
						<template
							v-for="keyword in addList.keywords"
							pa-0
							class="text-xs-center"
						>
							<v-chip
								:key="keyword.id"
								label
								color="accent darken-2"
								class="white--text"
							>
								<div class="subheading">{{ keyword.keyword }}</div>
								<v-btn
									class="small-btn"
									flat
									icon
									color="red darken-4"
									@click="deleteKeyword(keyword, addList)"
								>
									<v-icon>close</v-icon>
								</v-btn>
							</v-chip>
						</template>
					</v-layout>
				</v-container>
			</v-card>
		</v-dialog>

		<v-dialog
			v-model="rename"
			max-width="800"
		>
			<v-card v-if="rename">
				<EditKeywordList
					:list-name="renameList.name"
					:whole-words-only="renameList.wholeWordsOnly"
					:case-sensitive="renameList.caseSensitive"
					:existing-names="keywordListsNames.filter(v => v !== renameList.name)"
					@update-name="updateKeywordList"
					@close="rename = false"
				/>
			</v-card>
		</v-dialog>
		<Confirm ref="confirm"/>
	</v-container>
</template>

<script>
import KeywordIterator from '@/components/Report/KeywordIterator';
import EditKeywordList from '@/components/Report/EditKeywordList';
import NewItem from '@/components/NewItem';
import Confirm from '@/components/Confirm';
import Validation from '@/mixins/Validation';
import { Keyword, KeywordList, KeywordListDefault } from '@/models/Keyword';

export default {
	components: {
		KeywordIterator,
		EditKeywordList,
		NewItem,
		Confirm,
	},
	mixins: [Validation],
	data() {
		return {
			show: false,
			valid: { new: true, add: true, rename: true },
			add: false,
			addList: {},
			rename: false,
			renameList: {},
			name: '',
			keyword: '',
		};
	},
	created() {
		this.show = this.$store.get('isSkilled', 'keywords');
		if (!this.show) {
			this.$router.replace({ name: 'NotFound' });
		}
	},
	computed: {
		project() {
			return this.$store.get('projects/currentProject');
		},
		keywordLists() {
			const keywordLists = this.$store.get('keywordLists');
			keywordLists.sort((a, b) => {
				const isDefault = b.isDefault - a.isDefault;
				if (isDefault !== 0) return isDefault;
				if (a.name.toLowerCase() < b.name.toLowerCase()) return -1;
				if (a.name.toLowerCase() > b.name.toLowerCase()) return 1;
				return 0;
			});

			return keywordLists;
		},
		addListKeywords() {
			if (!this.addList.keywords) {
				return [];
			}
			const keywords = this.addList.keywords.map(o => o.keyword);
			return keywords;
		},
		keywordListsNames() {
			const names = this.keywordLists.map(o => o.name);
			return names;
		},
	},
	methods: {
		newKeywordList() {
			this.valid.new = false;
			const keywordLists = this.keywordLists;
			const model = new KeywordList();
			model.name = this.name.trim().replace(/\s+/g, ' ');
			model.projectId = this.$store.get('projects/currentProject').id;
			model.save().then(() => {
				// Set empty array for model so we can immediately add to it
				model.keywords = [];
				keywordLists.push(model);
				this.$notify('success', `Keyword list ${this.name} added.`);
			}).catch(() => {
				this.$notify('error', `Failed to add keyword list ${model.name}.`);
			}).finally(() => {
				this.closeNewList();
				this.valid.new = true;
			});
		},
		deleteKeywordList(list) {
			const listName = list.name;
			const keywordLists = this.$store.get('keywordLists');

			this.$refs.confirm.createConfirm(`This will delete keyword list "${listName}".`, 'error')
				.then((answer) => {
					if (!answer) {
						return;
					}
					list.delete().then(() => {
						keywordLists.forEach((item, index, array) => {
							if (!item.name) {
								array.splice(index, 1);
							}
						});
						this.$notify('success', `Keyword list ${listName} deleted.`);
					}).catch(() => {
						this.$notify('error', `Failed to delete keyword list ${listName}.`);
					});
				});
		},
		updateKeywordList(values) {
			this.renameList.set('name', values.name);
			this.renameList.set('wholeWordsOnly', values.wholeWordsOnly);
			this.renameList.set('caseSensitive', values.caseSensitive);
			this.renameList.save().then(() => {
				this.$notify('success', `Keyword list ${this.renameList.name} has been updated.`);
			}).catch(() => {
				this.renameList.reset();
				this.$notify('error', `Failed to update keyword list ${this.renameList.name}.`);
			});
			this.rename = false;
		},
		setDefault(list) {
			const keywordLists = this.keywordLists;
			const defaultList = new KeywordListDefault();
			defaultList.id = list.id;
			defaultList.save().then(() => {
				keywordLists.forEach((keywordList) => {
					keywordList.isDefault = false;
				});
				list.isDefault = true;
				this.$notify('success', `Keyword list ${list.name} has been set as default.`);
			}).catch(() => {
				this.$notify('error', `Failed to set keyword list ${list.name} as default.`);
			});
		},
		addKeyword(list) {
			const keywordLists = this.keywordLists;
			const keywordListIndex = keywordLists.map(o => o.id).indexOf(list.id);

			const model = new Keyword();
			model.keywordListId = list.id;
			model.keyword = list.new.trim().replace(/\s+/g, ' ');
			model.projectId = list.projectId;
			model.save().then(() => {
				keywordLists[keywordListIndex].keywords.push(model);
			}).catch(() => {
				this.$notify('error', `Failed to add keyword ${model.keyword}.`);
			});
			this.$refs.addForm.reset();
		},
		deleteKeyword(keyword, list) {
			const keywordIndex = list.keywords.indexOf(keyword);
			keyword.delete().then(() => {
				// If newly added, the keyword will still exist with null id and must be
				// manually removed
				if (list.keywords[keywordIndex] && !list.keywords[keywordIndex].id) {
					list.keywords.splice(keywordIndex, 1);
				}
			}).catch(() => {
				this.$notify('error', `Failed to delete keyword ${keyword.keyword}.`);
			});
		},
		showAdd(list) {
			this.addList = list;
			this.add = true;
		},
		showRename(list) {
			this.renameList = list;
			this.rename = true;
		},
		closeNewList() {
			this.$refs.newItem.close();
			this.$refs.keywordsForm.reset();
		},
	},
};
</script>

<style lang="scss" scoped>
.small-btn {
	margin-top: 0;
	margin-right: -10px;
	margin-bottom: 0;
}
.absolute--right {
	position: absolute;
	top: 0;
	right: 0;
}
</style>
